import React from 'react';

import { Button, Checkbox, Flex, Image, Table, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import i18n from 'segments/i18n';
import { defaultPaginationConfig } from 'segments/utils/constants/common';

import {
  convertCourtsToTable,
  CourtInfo,
  courtsSurfaceTagColors,
  courtsTypeTagColors,
  CourtSurfaceType,
  courtSurfaceTypesTittle,
  CourtType,
  courtTypesTittle,
  useDeleteCourtMutation,
  useToggleCourtActivate
} from 'features/courts';
import { TimesPriceRow } from 'features/timesPrice';

export interface TableData extends CourtInfo {
  key: number;
}

type Props = {
  items: CourtInfo[];
  itemsTotal?: number;
  isLoading?: boolean;
  currentPage?: number;
  defaultPageSize?: number;
  onPageChange?: (page: number, pageSize: number) => void;
  onCreateClick: () => void;
  onEditClick: (courtId: number) => void;
};

export const CourtsTable = (props: Props) => {
  const {
    items,
    itemsTotal = 0,
    isLoading,
    currentPage = 1,
    defaultPageSize = 30,
    onPageChange,
    onEditClick,
    onCreateClick,
  } = props;

  const { isLoading: isToggleActivateLoading, toggleCourtActivate } = useToggleCourtActivate();
  const [deleteCourt, { isLoading: isCoartDeleting }] = useDeleteCourtMutation();
  const { t } = useTranslation(undefined, { keyPrefix: 'table' });

  return (
    <Table<TableData>
      size="small"
      dataSource={convertCourtsToTable(items)}
      loading={isLoading}
      scroll={{ x: 1200 }}
      expandable={{
        expandedRowRender: (record) => (<TimesPriceRow dataSource={record.metainfo.pricing} />)
      }}
      pagination={{
        ...defaultPaginationConfig,
        defaultPageSize,
        current: currentPage,
        total: itemsTotal,
        onChange: onPageChange,
      }}
    >
      <Table.Column<TableData>
        title={t('id', 'ID')}
        dataIndex="id"
        width={50}
      />
      <Table.Column<TableData>
        title={t('name', 'Названия')}
        dataIndex="name"
        width={160}
      />
      <Table.Column<TableData>
        title={t('image', 'Изоброжение')}
        dataIndex="imageUrl"
        render={(url: string) => (
          url === null
            ? <Typography.Text style={{ fontSize: '12px' }}>{i18n.t('image-store.errors.not-seted')}</Typography.Text>
            : <Image height={40} src={url} alt={i18n.t('image-store.errors.not-founded')} style={{ fontSize: '10px' }} />
        )}
        width={100}
      />
      <Table.Column<TableData>
        title={t('type')}
        dataIndex="type"
        width={120}
        render={(type: CourtType) => (
          <Tag color={courtsTypeTagColors[type]}>
            {courtTypesTittle[type].toUpperCase()}
          </Tag>
        )}
      />
      <Table.Column<TableData>
        title={t('surface-type')}
        dataIndex="surfaceType"
        width={80}
        render={(type: CourtSurfaceType) => (
          <Tag color={courtsSurfaceTagColors[type]}>
            {courtSurfaceTypesTittle[type].toUpperCase()}
          </Tag>
        )}
      />
      <Table.Column<TableData>
        title={t('activated')}
        dataIndex="active"
        render={(isActive: boolean, { id }) => (
          <Flex
            justify='left'
          >
            <Checkbox
              disabled={isToggleActivateLoading}
              checked={isActive}
              onClick={() => {
                toggleCourtActivate({ courtId: id, state: isActive });
              }}
            />
          </Flex>
        )}
      />
      <Table.Column<TableData>
        dataIndex="id"
        width={60}
        render={(id: number) => (
          <Button type="primary" onClick={() => onEditClick(id)}>
            {t('edit', 'Редактировать')}
          </Button>
        )}
      />
      <Table.Column<TableData>
        dataIndex="id"
        width={60}
        title={onCreateClick && <Button type="primary" onClick={onCreateClick}>{t('create')}</Button>}
        render={(id: number) => (
          <Button type="primary" danger disabled={isCoartDeleting} onClick={() => {
            deleteCourt(id);
          }}
          >
            {t('delete', 'Удалить')}
          </Button>
        )}
      />
    </Table>
  );
};
